import classnames from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { CartBaseProduct } from '@dustin-web/product-components/src/hooks/cart/cart-base-product-types'
import { UpdateCart } from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import { FormattedCurrency } from '@dustin-web/microsite-components/src/i18n'
import { getOriginalPrice, getPrice } from '@dustin-web/microsite-data/src/utils'
import { CartItemFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import ProductAvailability from '@dustin-web/product-components/src/product-availability'

import { DigitalDataOrigin } from '@dustin-web/product-components/src/utils/digital-data-types'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen'
import CartItemQuantity from './cart-item-quantity'
import {
  handeOnClickProduct,
  pushViewItemList,
} from '@dustin-web/product-components/src/cards/utils'
import EndUserInformation from './cart-end-user-information'
import ProductHeadingLink from './cart-heading'
import CartItemUtilityRow from './cart-item-utility-row'
import FavoritesCartItemUtilityRow from './cart-item-utility-row-favorites'
import CartItemImage from './cart-item-image'
import MaxAvailableError from './errors/max-available-error'

import { pushEvent } from '@cartsite-app/src/utils/tracking'
import { Icon } from '@dustin-web/microsite-components/src/icon-library'

type Props = {
  item: CartItemFragment
  childItems?: CartItemFragment[]
  updateCart: UpdateCart
  showPriceIncVat: boolean
  parentQuantity?: number
  inFavorites?: boolean
  disableEditing?: boolean
  isFirstItem?: boolean
  digitalDataIndex: number
  digitalDataOrigin: DigitalDataOrigin
  digitalDataOriginChild?: 'favoritesPageAccessories' | 'shoppingCartAccessories'
}

const CartItem = ({
  item,
  childItems,
  updateCart,
  showPriceIncVat,
  parentQuantity,
  inFavorites = false,
  disableEditing = false,
  isFirstItem = false,
  digitalDataIndex,
  digitalDataOrigin,
  digitalDataOriginChild,
}: Props) => {
  const { product, quantity, mainProductErpIdentifier, parentCartItemId, comment } = item
  const { currencyCode } = useSiteContext()
  const { translate, incExVatTranslation } = useTranslations()
  const [animateOut, setAnimateOut] = useState(false)
  const [oldQuantity, setOldQuantity] = useState(quantity) // Variable to compare with newQuantity to send the right quantityDiff in DigitalData event. quantity from useShoppingCart is updated while editing quantity in text input field so will send wrong quantityDiff
  const [preventSetOldQuantity, setPreventSetOldQuantity] = useState(false)

  const priceData = product?.price
  const maxAvailableQuantity = item.product?.availability?.maxAvailableQuantity
    ? item.product?.availability?.maxAvailableQuantity
    : 0
  const maxAvailableQuantityError =
    item.product?.availability?.maxAvailableQuantity != null
      ? item.product?.availability?.maxAvailableQuantity < item.quantity
      : false

  useEffect(() => {
    if (!preventSetOldQuantity) {
      // Don't set oldQuantity while editing quiantity in input field
      setOldQuantity(quantity)
    }
  }, [quantity, preventSetOldQuantity, oldQuantity])

  const onCartUpdate = useCallback(
    async (
      product: CartBaseProduct,
      mainProductErpIdentifier: string | null,
      newQuantity: number,
      comment?: string | null,
      parentCartItemId?: number | null,
      _preventRemovalOfItem?: boolean,
      eventOrigin?: string
    ) => {
      if (eventOrigin === 'input') {
        // Only setOldQuantity when input field is not focused
        setPreventSetOldQuantity(true)
      }

      const shouldUdateCart: boolean =
        eventOrigin === 'input' || eventOrigin === 'plusSign' || eventOrigin === undefined // Only allow certain events to update cart to save calls to DB

      if (shouldUdateCart) {
        updateCart(product, mainProductErpIdentifier, newQuantity, comment, parentCartItemId)
      }

      const shouldSendTrackingEvent =
        !inFavorites &&
        (eventOrigin === 'buyButton' || eventOrigin === 'plusSign' || eventOrigin === undefined) // Should only send tracking event when quantity is set - not when textfield has focus

      if (shouldSendTrackingEvent) {
        const event =
          newQuantity === 0 || newQuantity < oldQuantity ? 'removeFromCart' : 'addToCart'

        pushEvent({
          event,
          product,
          oldQuantity,
          newQuantity,
          digitalDataIndex,
          digitalDataOrigin,
          translate,
          currencyCode,
        })

        setOldQuantity(newQuantity)
        setPreventSetOldQuantity(false)
      }
    },
    [inFavorites, oldQuantity, translate, updateCart, digitalDataIndex, digitalDataOrigin]
  )

  const updateQuantity = useCallback(
    (newQuantity: number, eventOrigin?: string) =>
      product &&
      onCartUpdate(
        product,
        mainProductErpIdentifier || null,
        newQuantity,
        comment,
        parentCartItemId,
        false,
        eventOrigin
      ),
    [product, mainProductErpIdentifier, onCartUpdate]
  )

  const removeCartItem = useCallback(() => {
    if (product) {
      setAnimateOut(true)
      setTimeout(() => {
        onCartUpdate(product, mainProductErpIdentifier || null, 0, comment, parentCartItemId)
      }, 800)
    }
  }, [product, mainProductErpIdentifier, onCartUpdate])

  if (!product || !priceData) {
    return null
  }

  const price = getPrice(priceData, showPriceIncVat)
  const originalPrice = getOriginalPrice(priceData, showPriceIncVat)
  const isAvailableForSale = !!product.availability?.isAvailableForSale || item.isMandatory

  function trackPushSelectItem() {
    handeOnClickProduct(digitalDataOrigin, product as any, digitalDataIndex)
  }

  return (
    <li className={classnames({ 'a-fade-and-collapse-500': animateOut })}>
      <Wrapper
        parentCartItemId={parentCartItemId}
        maxAvailableQuantityError={maxAvailableQuantityError}
        digitalDataIndex={digitalDataIndex}
        digitalDataOrigin={digitalDataOrigin}
        item={item}>
        <div className="c-shoppingcart__item-image">
          <CartItemImage product={product} onClickTracker={trackPushSelectItem} />
        </div>

        <div className="c-shoppingcart__item-info">
          <div className="c-shoppingcart__item-title">
            {digitalDataOrigin === 'favoritesPage' ? (
              <h3 className="u-mb-8 u-fs-16">
                <ProductHeadingLink product={product} trackPushSelectItem={trackPushSelectItem} />
              </h3>
            ) : (
              <h2 className="u-mb-8">
                <ProductHeadingLink product={product} trackPushSelectItem={trackPushSelectItem} />
              </h2>
            )}
            <small className="d-none d-md-block u-color-mute">
              {translate('ArticleNumber')}: {product.productErpIdentifier}
            </small>
          </div>

          <div className="c-shoppingcart__item-unit-price">
            {isFirstItem && (
              <span className="c-shoppingcart__column-header">{translate('Cart_UnitPrice')}</span>
            )}
            {priceData.isCampaign && item.product?.availability?.isAvailableForSale && (
              <>
                <span className="c-price c-price--original u-fw-normal">
                  {!isFirstItem && (
                    <span className="visually-hidden">{translate('Cart_UnitPrice')}</span>
                  )}
                  <FormattedCurrency amount={originalPrice} />
                </span>
                <span className="c-price c-price--campaign u-fw-normal">
                  {!isFirstItem && (
                    <span className="visually-hidden">{translate('Cart_UnitPrice')}</span>
                  )}
                  <FormattedCurrency amount={price} />
                </span>
              </>
            )}
            {item.product?.availability?.isAvailableForSale && !priceData.isCampaign && (
              <>
                {!isFirstItem && (
                  <span className="visually-hidden">{translate('Cart_UnitPrice')}</span>
                )}
                <FormattedCurrency amount={price} />
              </>
            )}
          </div>

          <div className="c-shoppingcart__item-quantity">
            <CartItemQuantity
              onSetQuantity={updateQuantity}
              quantity={quantity}
              product={product}
              isMandatoryAccessory={item.isMandatory}
              disableEditing={disableEditing}
              isFirstItem={isFirstItem}
              parentQuantity={parentQuantity}
            />
          </div>

          <div className="c-shoppingcart__item-total-price">
            {isFirstItem && (
              <span className="c-shoppingcart__column-header">{translate('Cart_Total')}</span>
            )}

            <div className="u-mb-4 u-mb-md-0">
              <div className={'col u-text-right u-px-0'}>
                {isAvailableForSale && (
                  <>
                    {!isFirstItem && (
                      <span className="visually-hidden">{translate('Cart_Total')}</span>
                    )}
                    <FormattedCurrency amount={price * quantity} />
                  </>
                )}
              </div>
              {isAvailableForSale && (
                <>
                  {!isFirstItem && (
                    <span className="visually-hidden">{translate('Cart_Total')}</span>
                  )}
                  <span className="c-price-vat">
                    {incExVatTranslation(product.price?.priceVat)}
                  </span>
                </>
              )}
            </div>
          </div>

          {!item.isMandatory && !disableEditing && !inFavorites && (
            <div className="c-shoppingcart__remove-button">
              <button className="c-shoppingcart__remove-button__icon" onClick={removeCartItem}>
                <Icon name="trash-bin" className="i-12" />
              </button>
            </div>
          )}

          <div className="c-shoppingcart__item-availability">
            <ProductAvailability
              availability={product.availability ?? undefined}
              isBid={product.price?.isBid}
              listForm={true}
            />
          </div>

          <EndUserInformation item={item} />

          {
            inFavorites ? (
              <FavoritesCartItemUtilityRow
                comment={comment ?? ''}
                availability={product.availability}
                cartItem={item}
                disableEditing={disableEditing}
                digitalDataOrigin={digitalDataOrigin}
                digitalDataIndex={digitalDataIndex}
                removeCartItem={removeCartItem}
              />
            ) : (
              <CartItemUtilityRow
                comment={comment ?? ''}
                availability={product.availability}
                mainProductErpIdentifier={mainProductErpIdentifier}
                productErpIdentifier={product.productErpIdentifier}
                cartItem={item}
                disableEditing={disableEditing}
              />
            )
          }
        </div>

        {maxAvailableQuantityError && (
          <MaxAvailableError maxAvailableQuantity={maxAvailableQuantity} />
        )}

        {childItems && childItems.length > 0 && (
          <ul className="c-shoppingcart__selected-accessories ul-stripped arrow_up list-divider">
            {childItems.map((child, index) => (
              <CartItem
                item={child}
                key={child.id + index}
                digitalDataOrigin={digitalDataOriginChild as DigitalDataOrigin}
                digitalDataIndex={digitalDataIndex}
                updateCart={onCartUpdate}
                showPriceIncVat={showPriceIncVat}
                parentQuantity={item.quantity}
              />
            ))}
          </ul>
        )}
      </Wrapper>
    </li>
  )
}

type WrapperProps = {
  parentCartItemId?: number | null
  maxAvailableQuantityError: boolean
  digitalDataIndex: Props['digitalDataIndex']
  digitalDataOrigin: Props['digitalDataOrigin']
  item: Props['item']
  children: React.ReactNode
}

const Wrapper = ({
  parentCartItemId,
  maxAvailableQuantityError,
  digitalDataIndex,
  digitalDataOrigin,
  item,
  children,
}: WrapperProps) => {
  const ref = useTrackingOnScreen(() =>
    pushViewItemList(digitalDataIndex, item.product || undefined, digitalDataOrigin)
  )
  return (
    <div
      className={classnames('c-shoppingcart__item', {
        ['c-shoppingcart__item--selected-accessory']: !!parentCartItemId,
        'c-shoppingcart__item--error': maxAvailableQuantityError,
      })}
      ref={ref}>
      {children}
    </div>
  )
}
export default CartItem
